import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import CropModal from '../components/CropModal';
import GroupCard, { AVATAR_GRADIENTS } from '../components/GroupCard';
import GroupFormModal from '../components/GroupFormModal';
import ErrorBanner from '../components/ErrorBanner';
import GoogleButton from '../components/GoogleButton';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const LEVEL_LABELS = {
  loisir:        'Loisir',
  departemental: 'Départemental',
  regional:      'Régional',
  national:      'National',
  pro:           'Pro',
};
const LEVELS = [['loisir','Loisir'],['departemental','Dép.'],['regional','Régio.'],['national','Nat.'],['pro','Pro']];
const GENDER_LBL = { mix: 'Mixte', masculin: 'Masculin', feminin: 'Féminin' };
const SURF_LBL = { beach: 'Beach', green: 'Green', gymnase: 'Gymnase' };

function SField({ label, type = 'text', placeholder, value, onChange }) {
  return (
    <div className="card col gap-2" style={{ padding: 14, minWidth: 0 }}>
      <span className="mono" style={{ color: 'var(--text-3)' }}>{label}</span>
      <input type={type} placeholder={placeholder} value={value} onChange={onChange}
        style={{ flex: 1, background: 'transparent', border: 0, outline: 'none', minWidth: 0,
          color: 'var(--text)', fontSize: 14, fontFamily: 'inherit' }} />
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ padding: '0 20px', margin: '22px 0 12px' }}>
      <h3 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-2)', margin: 0 }}>
        {children}
      </h3>
    </div>
  );
}

function SegmentRow({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      {options.map(([k, l]) => (
        <button key={k} type="button" onClick={() => onChange(k)} style={{
          flex: 1, minWidth: 0, padding: '10px 0', borderRadius: 8, fontSize: 13, fontWeight: 600,
          background: value === k ? 'var(--text)' : 'var(--ink-3)',
          color: value === k ? 'var(--ink)' : 'var(--text-2)',
        }}>{l}</button>
      ))}
    </div>
  );
}


function TrophyIcon({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 4h10v5a5 5 0 0 1-10 0V4z"/>
      <path d="M7 6H4v2a3 3 0 0 0 3 3M17 6h3v2a3 3 0 0 1-3 3"/>
      <path d="M9 17h6M10 21h4M12 14v3"/>
    </svg>
  );
}

function PerformanceRow({ d }) {
  const parts = (d.name || '').split(/[·\s]+/).filter(Boolean);
  const head = parts[0] || d.name;
  const tail = parts.slice(1).join(' · ');
  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, gap: 8 }}>
        <div>
          <span style={{ fontSize: 15, fontWeight: 700 }}>{head}</span>
          {tail && <span style={{ color: 'var(--text-3)', fontWeight: 500, marginLeft: 6 }}>{tail}</span>}
        </div>
        <span className="mono" style={{ color: 'var(--text-3)', flexShrink: 0 }}>{d.played} tournois</span>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <span className="mono" style={{ color: 'var(--text-3)' }}>Win rate</span>
          <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginTop: 1 }}>{d.winrate}</span>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <span className="mono" style={{ color: 'var(--text-3)' }}>Victoires</span>
          <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--lime, #6BAA1E)', marginTop: 1 }}>{d.wins}</span>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <span className="mono" style={{ color: 'var(--text-3)' }}>Podiums</span>
          <span className="chip chip-sun" style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <TrophyIcon size={11} /> {d.podiums}
          </span>
        </div>
      </div>
    </div>
  );
}



function Profile({ user, onLogout, onUserUpdate }) {
  const navigate = useNavigate();
  const { userId: routeUserId } = useParams();
  const isOwn = !routeUserId || routeUserId === user?.id;
  const token = localStorage.getItem('token');

  // ── Profil d'un AUTRE utilisateur (lecture seule) ──
  const [other, setOther] = useState(null);
  const [profileError, setProfileError] = useState(false);
  const [otherGroups, setOtherGroups] = useState([]);
  const [followStatus, setFollowStatus] = useState('none'); // 'none' | 'pending' | 'following'
  const [followLoading, setFollowLoading] = useState(false);
  const [msgLoading, setMsgLoading] = useState(false);

  useEffect(() => {
    if (isOwn) return;
    const h = { Authorization: `Bearer ${token}` };
    setOther(null);
    setProfileError(false);
    axios.get(`${API_URL}/api/users/${routeUserId}/profile`, { headers: h })
      .then(r => { setOther(r.data); setFollowStatus(r.data.followStatus || (r.data.isFollowing ? 'following' : 'none')); })
      .catch(() => { setProfileError(true); });
    axios.get(`${API_URL}/api/users/${routeUserId}/groups`, { headers: h })
      .then(async r => {
        const groups = r.data || [];
        const withStats = await Promise.all(groups.map(async g => {
          try {
            const sr = await axios.get(`${API_URL}/api/groups/${g.id}/member-stats`, { headers: h });
            const statsMap = {}; (sr.data || []).forEach(s => { statsMap[s.id] = s; });
            return { ...g, memberDetails: (g.memberDetails || []).map(m => ({ ...m, ...(statsMap[m.id] || {}) })) };
          } catch { return g; }
        }));
        setOtherGroups(withStats);
      })
      .catch(() => {});
  }, [routeUserId, isOwn, token]);

  const handleFollow = async () => {
    if (followLoading || !other) return;
    setFollowLoading(true);
    try {
      if (followStatus === 'none') {
        const r = await axios.post(`${API_URL}/api/users/${other.id}/follow`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setFollowStatus(r.data.followStatus || 'following'); // 'pending' si profil privé
      } else {
        await axios.delete(`${API_URL}/api/users/${other.id}/follow`, { headers: { Authorization: `Bearer ${token}` } });
        setFollowStatus('none');
      }
    } catch { /* keep state */ } finally { setFollowLoading(false); }
  };

  // Demandes d'abonnement reçues (profil perso, mode privé)
  useEffect(() => {
    if (!isOwn) return;
    axios.get(`${API_URL}/api/users/me/follow-requests`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setFollowRequests(r.data || [])).catch(() => {});
  }, [isOwn, token]);

  const respondRequest = async (reqId, accept) => {
    try {
      if (accept) await axios.post(`${API_URL}/api/users/me/follow-requests/${reqId}/accept`, {}, { headers: { Authorization: `Bearer ${token}` } });
      else await axios.delete(`${API_URL}/api/users/me/follow-requests/${reqId}`, { headers: { Authorization: `Bearer ${token}` } });
      setFollowRequests(rs => rs.filter(r => r.id !== reqId));
    } catch { /* ignore */ }
  };

  const handleMessageOther = async () => {
    if (msgLoading || !other) return;
    setMsgLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/messages/conversations`, { targetUserId: other.id },
        { headers: { Authorization: `Bearer ${token}` } });
      navigate(`/messages/${res.data.id}`);
    } catch { setMsgLoading(false); }
  };

  const [firstName, setFirstName]   = useState(user?.firstName || '');
  const [lastName, setLastName]     = useState(user?.lastName  || '');
  const [phone, setPhone]           = useState(user?.phone     || '');
  const [email, setEmail]           = useState(user?.email     || '');
  const [gender, setGender]         = useState(user?.gender    || '');
  const [level, setLevel]           = useState(user?.level     || '');
  const [birthdate, setBirthdate]   = useState(user?.birthdate || '');
  const [position, setPosition]     = useState(user?.position  || '');
  const [avatar, setAvatar]         = useState(user?.avatarUrl || null);
  const [chatPreference, setChatPreference] = useState(user?.chatPreference || 'group_only');
  const [isPrivate, setIsPrivate]   = useState(user?.isPrivate === true);
  const [followRequests, setFollowRequests] = useState([]);
  const [avatarChanged, setAvatarChanged] = useState(false);
  const [cropSrc, setCropSrc]       = useState(null);

  const [showPassword, setShowPassword] = useState(false);
  const [currentPwd, setCurrentPwd]     = useState('');
  const [newPwd, setNewPwd]             = useState('');
  const [confirmPwd, setConfirmPwd]     = useState('');
  const [googleError, setGoogleError]   = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);

  const [editMode, setEditMode]       = useState(false);
  const [saving, setSaving]           = useState(false);
  const [success, setSuccess]         = useState('');
  const [error, setError]             = useState('');
  const [searchParams] = useSearchParams();
  const [profileTab, setProfileTab]   = useState(searchParams.get('tab') === 'groups' ? 'groups' : 'stats');
  const [userTournaments, setUserTournaments] = useState([]);
  const [userGroups, setUserGroups]   = useState([]);
  const [groupModal, setGroupModal]   = useState(null); // null | 'create' | 'join'
  const [profileStats, setProfileStats] = useState(null); // from /api/users/:id/profile

  const loadUserGroups = async () => {
    const t = localStorage.getItem('token');
    if (!t || !user?.id) return;
    const h = { Authorization: `Bearer ${t}` };
    try {
      const r = await axios.get(`${API_URL}/api/groups`, { headers: h });
      const groups = r.data || [];
      // Enrichir chaque membre avec ses stats (tournois/victoires/winrate),
      // sinon GroupCard affiche des tirets (m.tournaments/m.wins indéfinis).
      const withStats = await Promise.all(groups.map(async g => {
        try {
          const sr = await axios.get(`${API_URL}/api/groups/${g.id}/member-stats`, { headers: h });
          const statsMap = {};
          (sr.data || []).forEach(s => { statsMap[s.id] = s; });
          return {
            ...g,
            memberDetails: (g.memberDetails || []).map(m => ({
              ...m,
              ...(statsMap[m.id] || {}),
              isMe: m.id === user?.id,
            })),
          };
        } catch { return g; }
      }));
      setUserGroups(withStats);
    } catch {
      setError('Impossible de charger les groupes');
    }
  };

  useEffect(() => {
    const t = localStorage.getItem('token');
    if (!t || !user?.id) return;
    const h = { Authorization: `Bearer ${t}` };
    axios.get(`${API_URL}/api/users/me/tournaments`, { headers: h })
      .then(r => setUserTournaments(r.data || []))
      .catch(() => setError('Impossible de charger les tournois'));
    loadUserGroups();
    axios.get(`${API_URL}/api/users/${user.id}/profile`, { headers: h })
      .then(r => setProfileStats(r.data))
      .catch(() => {});
  }, [user?.id]); // eslint-disable-line

  const refreshProfile = async () => {
    const r = await axios.get(`${API_URL}/api/auth/profile`, { headers: { Authorization: `Bearer ${token}` } });
    onUserUpdate?.(r.data);
  };

  const handleGoogleLink = async (credential) => {
    setGoogleError(''); setGoogleLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/link/google`, { credential }, { headers: { Authorization: `Bearer ${token}` } });
      await refreshProfile();
    } catch (err) {
      setGoogleError(err.response?.data?.error || 'Erreur lors de la liaison Google');
    } finally { setGoogleLoading(false); }
  };

  const handleGoogleUnlink = async () => {
    setGoogleError(''); setGoogleLoading(true);
    try {
      await axios.delete(`${API_URL}/api/auth/link/google`, { headers: { Authorization: `Bearer ${token}` } });
      await refreshProfile();
    } catch (err) {
      setGoogleError(err.response?.data?.error || 'Erreur lors de la dissociation Google');
    } finally { setGoogleLoading(false); }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setCropSrc(url);
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!gender) { setError('Veuillez sélectionner votre genre'); return; }
    if (!level)  { setError('Veuillez sélectionner votre niveau'); return; }
    if (showPassword && newPwd) {
      if (newPwd !== confirmPwd) { setError('Les mots de passe ne correspondent pas'); return; }
      if (newPwd.length < 6)    { setError('Le mot de passe doit contenir au moins 6 caractères'); return; }
      if (!currentPwd)          { setError('Veuillez entrer votre mot de passe actuel'); return; }
    }
    setSaving(true);
    try {
      const fn = firstName.trim(); const ln = lastName.trim();
      const body = {
        firstName: fn.charAt(0).toUpperCase() + fn.slice(1).toLowerCase(),
        lastName: ln.toUpperCase(),
        phone, email, gender, level, position: position || null, chatPreference, isPrivate,
        birthdate: birthdate || null,
      };
      if (avatarChanged)          body.avatarUrl = avatar;
      if (showPassword && newPwd) { body.currentPassword = currentPwd; body.newPassword = newPwd; }
      const res = await axios.put(`${API_URL}/api/auth/profile`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Profil mis à jour !');
      onUserUpdate?.(res.data.user);
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
      setShowPassword(false); setAvatarChanged(false);
      setTimeout(() => { setEditMode(false); setSuccess(''); }, 1000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  let hash = 0;
  for (const c of String(user?.id || '')) hash = (hash * 31 + c.charCodeAt(0)) & 0xFFFFFF;
  const avatarGrad = AVATAR_GRADIENTS[Math.abs(hash) % 6];

  const initials = (() => {
    const f = firstName.trim(); const l = lastName.trim();
    if (f && l) return (f[0] + l[0]).toUpperCase();
    return f ? f.slice(0, 2).toUpperCase() : '?';
  })();

  // On ne compte dans les stats que les tournois auxquels l'utilisateur a réellement
  // participé, c.-à-d. ceux où il a une équipe. /me/tournaments renvoie aussi les
  // tournois de ses groupes sans équipe inscrite (myTeam === null) — à exclure.
  const participatedTournaments = useMemo(
    () => userTournaments.filter(e => e.myTeam),
    [userTournaments]
  );

  const tournamentsWon = participatedTournaments.filter(e => (e.myTeam?.results?.placement || e.placement) === '1').length;
  const totalMatchWins   = participatedTournaments.reduce((s, e) => s + (e.myTeam?.results?.wins   || 0), 0);
  const totalMatchLosses = participatedTournaments.reduce((s, e) => s + (e.myTeam?.results?.losses || 0), 0);
  const winRate = (totalMatchWins + totalMatchLosses) > 0
    ? Math.round(totalMatchWins / (totalMatchWins + totalMatchLosses) * 100) + '%'
    : '—';

  const SURF_LABELS = { beach: 'Beach', green: 'Green', gymnase: 'Gymnase' };

  const byFormatSurface = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const groups = {};
    participatedTournaments.forEach(entry => {
      const t = entry.tournament || entry;
      if (!t.date || t.date >= today) return; // past only
      const fmt = t.playerFormat || t.playerFormats?.[0] || '?';
      const surf = t.surface ? SURF_LABELS[t.surface] || t.surface : null;
      const key = surf ? `${fmt} · ${surf}` : fmt;
      if (!groups[key]) groups[key] = { name: key, played: 0, matchWins: 0, matchLosses: 0, podiums: 0 };
      groups[key].played++;
      const results = entry.myTeam?.results;
      if (results) {
        groups[key].matchWins   += results.wins   || 0;
        groups[key].matchLosses += results.losses || 0;
        const p = results.placement;
        if (p === '1' || p === '2' || p === '3') groups[key].podiums++;
      }
    });
    return Object.values(groups).map(d => ({
      ...d, wins: d.matchWins,
      winrate: (d.matchWins + d.matchLosses) > 0
        ? Math.round(d.matchWins / (d.matchWins + d.matchLosses) * 100) + '%' : '—',
    }));
  }, [participatedTournaments]); // eslint-disable-line

  const pastTournaments = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return participatedTournaments.filter(entry => {
      const t = entry.tournament || entry;
      return t.date && t.date < today;
    });
  }, [participatedTournaments]);

  // Tournois à venir = uniquement ceux où l'utilisateur a une équipe.
  const upcomingTournaments = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return participatedTournaments.filter(entry => {
      const t = entry.tournament || entry;
      return t.date && t.date >= today;
    });
  }, [participatedTournaments]);

  return (
    <>
      {cropSrc && (
        <CropModal
          src={cropSrc}
          onConfirm={(b64) => { setAvatar(b64); setAvatarChanged(true); URL.revokeObjectURL(cropSrc); setCropSrc(null); }}
          onCancel={() => { URL.revokeObjectURL(cropSrc); setCropSrc(null); }}
        />
      )}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, background: 'var(--ink)' }}>
        {!isOwn ? (
          /* ──────────────── PROFIL D'UN AUTRE UTILISATEUR (lecture seule) ──────────────── */
          !other ? (
            profileError
              ? <ErrorBanner message="Impossible de charger ce profil." onRetry={() => { setProfileError(false); setOther(null); const h = { Authorization: `Bearer ${token}` }; axios.get(`${API_URL}/api/users/${routeUserId}/profile`, { headers: h }).then(r => { setOther(r.data); setFollowStatus(r.data.followStatus || (r.data.isFollowing ? 'following' : 'none')); }).catch(() => { setProfileError(true); }); }} style={{ margin: 40 }} />
              : <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--text-3)' }}>Chargement…</div>
          ) : (() => {
            const oInit = ((other.firstName?.[0] || '') + (other.lastName?.[0] || '')).toUpperCase();
            const code = (other.id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
            const oGrad = AVATAR_GRADIENTS[Math.abs(code) % AVATAR_GRADIENTS.length];
            const oTotal = (other.stats.wins || 0) + (other.stats.losses || 0);
            const oWin = oTotal > 0 ? Math.round(other.stats.wins / oTotal * 100) + '%' : '—';
            const surfLbl = { beach: 'Beach', green: 'Green', gymnase: 'Gymnase' };
            const mkRow = (name, x, podiums = 0) => { const tot = (x.wins || 0) + (x.losses || 0); return { name, played: x.tournaments, wins: x.wins, podiums, winrate: tot > 0 ? Math.round(x.wins / tot * 100) + '%' : '—' }; };
            // Découpage combiné format · surface (aligné sur le profil perso). Fallback sur les anciens
            // découpages séparés tant que le backend ne renvoie pas formatSurfaceStats (fenêtre de déploiement).
            const perfRows = other.formatSurfaceStats
              ? other.formatSurfaceStats.map(x => mkRow(x.surface ? `${x.format} · ${surfLbl[x.surface] || x.surface}` : x.format, x, x.podiums || 0))
              : [
                  ...['2x2', '3x3', '4x4', '6x6'].filter(f => other.formatStats?.[f]).map(f => mkRow(f, other.formatStats[f])),
                  ...['beach', 'green', 'gymnase'].filter(s => other.surfaceStats?.[s]).map(s => mkRow(surfLbl[s], other.surfaceStats[s])),
                ];
            const canMsg = other.chatPreference !== 'none' && (other.chatPreference === 'all' || other.isGroupMember);
            return (
              <>
                {/* Hero */}
                <div className="tourney-hero sand-grain on-hero" style={{ padding: '0 20px 16px', flexShrink: 0 }}>
                  <div className="hero-photo photo-court" />
                  <div className="hero-tint" />
                  <div style={{ padding: '8px 0 14px' }}>
                    <button aria-label="Retour" onClick={() => navigate(-1)} style={{ width: 40, height: 40, borderRadius: '50%',
                      background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(8px)', border: 'none',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M11 6l-6 6 6 6"/>
                      </svg>
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: '0 0 auto', minWidth: 88, display: 'flex', justifyContent: 'flex-start' }}>
                      <button onClick={handleFollow} disabled={followLoading} style={{ padding: '9px 12px', borderRadius: 999, fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', cursor: 'pointer', border: followStatus !== 'none' ? '1px solid rgba(255,255,255,0.28)' : 'none', background: followStatus !== 'none' ? 'rgba(255,255,255,0.16)' : 'var(--sun)', color: followStatus !== 'none' ? '#fff' : 'var(--ink)', backdropFilter: followStatus !== 'none' ? 'blur(8px)' : 'none' }}>
                        {/* ponytail: « En attente » et pas « Demande envoyée » — le bouton est
                            désormais dans une colonne latérale, le libellé long mangeait le nom. */}
                        {followStatus === 'following' ? 'Suivi ✓' : followStatus === 'pending' ? 'En attente' : 'Suivre'}
                      </button>
                    </div>
                    <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
                      <div style={{ width: 60, height: 60, borderRadius: '50%', border: '3px solid rgba(255,255,255,0.4)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: other.avatarUrl ? 'transparent' : oGrad, margin: '0 auto' }}>
                        {other.avatarUrl
                          ? <img src={other.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <span className="display" style={{ fontSize: 22, color: '#fff' }}>{oInit}</span>}
                      </div>
                      {/* ponytail: prénom seul — le nom complet passait sur 2-3 lignes entre les
                          deux boutons latéraux. L'avatar et les puces suffisent à identifier. */}
                      <div className="display" style={{ fontSize: 26, marginTop: 10 }}>{other.firstName}</div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                        {other.level && <span className="chip" style={{ color: 'rgba(255,255,255,0.9)', background: 'rgba(255,255,255,0.18)', borderColor: 'rgba(255,255,255,0.3)' }}>{LEVEL_LABELS[other.level] || other.level}</span>}
                        {other.position && <span className="chip" style={{ color: 'rgba(255,255,255,0.9)', background: 'rgba(255,255,255,0.18)', borderColor: 'rgba(255,255,255,0.3)' }}>{other.position.charAt(0).toUpperCase() + other.position.slice(1)}</span>}
                      </div>
                    </div>
                    <div style={{ flex: '0 0 auto', minWidth: 88, display: 'flex', justifyContent: 'flex-end' }}>
                      {canMsg && (
                        <button onClick={handleMessageOther} disabled={msgLoading} style={{ padding: '9px 12px', borderRadius: 999, background: 'rgba(255,255,255,0.16)', color: '#fff', border: '1px solid rgba(255,255,255,0.28)', backdropFilter: 'blur(8px)', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                          {msgLoading ? 'Ouverture…' : 'Message'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="screen-scroll" style={{ flex: 1 }}>
                  {/* Stats row */}
                  <div style={{ padding: '16px 20px 0' }}>
                    <div className="card row" style={{ padding: 4 }}>
                      {[
                        { v: other.stats.tournaments || '—', l: 'Tournois', color: 'var(--text)' },
                        { v: oWin, l: 'Win rate', color: 'var(--text)' },
                        { v: other.stats.titles || '—', l: 'Remportés', color: 'var(--sun-2)', icon: true },
                      ].map((s, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--line)' }} />}
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 0' }}>
                            <span className="display" style={{ fontSize: 22, color: s.color, display: 'inline-flex', alignItems: 'center', gap: 4 }}>{s.icon && <TrophyIcon size={16} />}{s.v}</span>
                            <span className="mono" style={{ color: 'var(--text-3)', marginTop: 2 }}>{s.l}</span>
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>

                  {/* Tabs */}
                  <div style={{ padding: '14px 20px 0', borderBottom: '1px solid var(--line)' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {[['stats', 'Stats'], ['groups', 'Groupes']].map(([id, lbl]) => (
                        <button key={id} onClick={() => setProfileTab(id)} style={{ padding: '10px 14px', position: 'relative', fontSize: 14, fontWeight: 600, color: profileTab === id ? 'var(--text)' : 'var(--text-3)', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                          {lbl}
                          {profileTab === id && <span style={{ position: 'absolute', bottom: -1, left: 14, right: 14, height: 2, background: 'var(--sun)', borderRadius: 2 }} />}
                        </button>
                      ))}
                    </div>
                  </div>

                  {profileTab === 'stats' && (
                    <>
                      {perfRows.length > 0 && (
                        <>
                          <div className="section-title" style={{ marginTop: 24 }}><h3>Performance par format</h3></div>
                          <div className="section col gap-2">{perfRows.map((d, i) => <PerformanceRow key={i} d={d} />)}</div>
                        </>
                      )}
                      {other.partners?.length > 0 && (
                        <>
                          <div className="section-title" style={{ marginTop: 24 }}><h3>Partenaires fréquents</h3></div>
                          <div className="section col gap-2">
                            {other.partners.map((p, i) => (
                              <div key={p.id || i} className="card row gap-3" style={{ padding: 12, cursor: 'pointer' }} onClick={() => p.id && navigate(`/profil/${p.id}`)}>
                                <div className="av-circle av-sm" style={{ flexShrink: 0 }}>
                                  {p.avatarUrl ? <img src={p.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (p.firstName?.[0] || '?') + (p.lastName?.[0] || '')}
                                </div>
                                <div className="col" style={{ flex: 1, minWidth: 0 }}>
                                  <span style={{ fontSize: 14, fontWeight: 600 }}>{p.firstName} {p.lastName}</span>
                                  <span className="mono" style={{ color: 'var(--text-3)', marginTop: 2 }}>{p.tournaments} tournoi{p.tournaments !== 1 ? 's' : ''} ensemble</span>
                                </div>
                                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--lime, #6BAA1E)' }}>{p.wins}V</div>
                                  <div style={{ fontSize: 11, color: '#E5503E' }}>{p.losses}D</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                      {other.history?.length > 0 ? (
                        <>
                          <div className="section-title" style={{ marginTop: 24 }}><h3>Historique</h3><span className="mono" style={{ color: 'var(--text-3)' }}>{other.history.length}</span></div>
                          <div className="section col gap-2">
                            {other.history.map((h, i) => {
                              const dateStr = h.date ? new Date(h.date + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
                              return (
                                <div key={i} className="card row gap-3" style={{ padding: 12, cursor: 'pointer' }} onClick={() => h.id && navigate(`/tournaments/${h.id}`)}>
                                  <div style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0, background: h.placement === '1' ? 'rgba(245,166,35,0.18)' : 'var(--ink-3)', color: h.placement === '1' ? 'var(--sun-2)' : 'var(--text-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {h.placement ? <TrophyIcon size={18} /> : <span className="mono" style={{ fontSize: 9 }}>{h.date ? new Date(h.date + 'T12:00:00').toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase() : '—'}</span>}
                                  </div>
                                  <div className="col" style={{ flex: 1, minWidth: 0 }}>
                                    <span style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.name}</span>
                                    <span className="mono" style={{ color: 'var(--text-3)', marginTop: 2 }}>{[dateStr, h.playerFormat, h.gender && (GENDER_LBL[h.gender] || h.gender), h.surface && (SURF_LBL[h.surface] || h.surface)].filter(Boolean).join(' · ')}</span>
                                  </div>
                                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                    {h.placement && <div style={{ fontSize: 13, fontWeight: 700, color: h.placement === '1' ? 'var(--sun-2)' : 'var(--text-2)', marginBottom: 2 }}>{h.placement === '1' ? '🏆 1er' : h.placement === '2' ? '🥈 2e' : h.placement === '3' ? '🥉 3e' : `#${h.placement}`}</div>}
                                    {(h.wins > 0 || h.losses > 0) && <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)' }}>{h.wins}V {h.losses}D</div>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      ) : perfRows.length === 0 && (
                        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                          <div style={{ fontSize: 40, marginBottom: 12 }}>🏐</div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>Aucun tournoi</div>
                          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Pas encore de tournois enregistrés.</div>
                        </div>
                      )}
                    </>
                  )}
                  {profileTab === 'groups' && (
                    <>
                      <div className="section-title" style={{ marginTop: 20 }}><h3>Groupes</h3><span className="mono" style={{ color: 'var(--text-3)' }}>{otherGroups.length}</span></div>
                      {otherGroups.length > 0 ? (
                        <div className="section col gap-3">{otherGroups.map((g, i) => <GroupCard key={i} g={g} />)}</div>
                      ) : (
                        <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>Aucun groupe public.</div>
                      )}
                      <div style={{ height: 28 }} />
                    </>
                  )}
                </div>
              </>
            );
          })()
        ) : editMode ? (
          /* ──────────────── EDIT MODE ──────────────── */
          <>
            <div style={{ padding: '4px 20px 12px', flexShrink: 0, borderBottom: '1px solid var(--line)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="display" style={{ fontSize: 18 }}>Modifier le profil</div>
                <button type="button" onClick={() => { setEditMode(false); setError(''); setSuccess(''); }}
                  style={{ color: 'var(--text-3)', fontSize: 13, fontWeight: 600,
                    background: 'none', border: 'none', padding: '8px 4px', cursor: 'pointer' }}>
                  Annuler
                </button>
              </div>
            </div>

            <div className="page-content" style={{ paddingBottom: 100 }}>
              {error   && <div style={{ margin: '12px 20px 0', padding: '10px 14px', borderRadius: 10, background: 'rgba(229,80,62,0.1)', color: '#E5503E', fontSize: 13, fontWeight: 600, border: '1px solid rgba(229,80,62,0.25)' }}>{error}</div>}
              {success && <div style={{ margin: '12px 20px 0', padding: '10px 14px', borderRadius: 10, background: 'rgba(107,170,30,0.1)', color: '#6BAA1E', fontSize: 13, fontWeight: 600, border: '1px solid rgba(107,170,30,0.25)' }}>{success}</div>}

              <form onSubmit={handleSubmit}>
                {/* Avatar */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 16, padding: '0 20px' }}>
                  <label style={{ cursor: 'pointer', position: 'relative' }}>
                    <div style={{ width: 80, height: 80, borderRadius: '50%', border: '3px solid var(--sun)',
                      background: avatar ? 'transparent' : avatarGrad,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                      {avatar
                        ? <img src={avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <span className="display" style={{ fontSize: 24, color: '#fff' }}>{initials}</span>
                      }
                    </div>
                    <span style={{ position: 'absolute', bottom: 0, right: 0, width: 26, height: 26, borderRadius: '50%',
                      background: 'var(--sun)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: '2px solid var(--ink)', fontSize: 14, fontWeight: 800 }}>+</span>
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
                  </label>
                  <span className="mono" style={{ color: 'var(--text-3)', marginTop: 8 }}>Changer la photo</span>
                </div>

                {/* Identité */}
                <SectionTitle>Identité</SectionTitle>
                <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <SField label="Prénom" placeholder="Prénom" value={firstName} onChange={e => setFirstName(e.target.value)} />
                    <SField label="Nom" placeholder="Nom" value={lastName} onChange={e => setLastName(e.target.value)} />
                  </div>
                  <SField label="E-mail" type="email" placeholder="email@exemple.fr" value={email} onChange={e => setEmail(e.target.value)} />
                  <SField label="Téléphone" type="tel" placeholder="06 12 34 56 78" value={phone} onChange={e => setPhone(e.target.value)} />
                </div>

                {/* Genre */}
                <SectionTitle>Genre</SectionTitle>
                <div style={{ padding: '0 20px' }}>
                  <div className="card col gap-2" style={{ padding: 14 }}>
                    <SegmentRow value={gender} onChange={setGender}
                      options={[['masculin','Homme'],['feminin','Femme']]} />
                  </div>
                </div>

                {/* Niveau */}
                <SectionTitle>Niveau</SectionTitle>
                <div style={{ padding: '0 20px' }}>
                  <div className="card col gap-2" style={{ padding: 14 }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {LEVELS.map(([val, lbl]) => (
                        <button key={val} type="button" onClick={() => setLevel(val)} style={{
                          flex: 1, minWidth: 0, padding: '10px 0', borderRadius: 8, fontSize: 12, fontWeight: 600,
                          background: level === val ? 'var(--sun)' : 'var(--ink-3)',
                          color: level === val ? 'var(--ink)' : 'var(--text-2)',
                        }}>{lbl}</button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Date de naissance */}
                <SectionTitle>Date de naissance</SectionTitle>
                <div style={{ padding: '0 20px' }}>
                  <SField label="Naissance" type="date" value={birthdate} onChange={e => setBirthdate(e.target.value)} />
                  <span style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4, display: 'block' }}>
                    Optionnel — utilisé pour vérifier l'âge des catégories jeunes.
                  </span>
                </div>

                {/* Poste */}
                <SectionTitle>Poste préféré</SectionTitle>
                <div style={{ padding: '0 20px' }}>
                  <div className="card col gap-2" style={{ padding: 14 }}>
                    <SegmentRow value={position || ''} onChange={(v) => setPosition(v === position ? '' : v)}
                      options={[['','Peu importe'],['passeur','Passeur'],['attaquant','Attaquant']]} />
                  </div>
                </div>

                {/* Confidentialité — toggle façon Instagram */}
                <SectionTitle>Confidentialité du compte</SectionTitle>
                <div style={{ padding: '0 20px' }}>
                  <div className="card row" style={{ padding: 14, justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>Compte privé</span>
                    <button type="button" role="switch" aria-checked={isPrivate} aria-label="Compte privé"
                      onClick={() => setIsPrivate(v => !v)}
                      style={{ width: 46, height: 28, borderRadius: 999, border: 'none', cursor: 'pointer', flexShrink: 0, padding: 3,
                        background: isPrivate ? 'var(--sun)' : 'var(--line-2)', display: 'inline-flex',
                        justifyContent: isPrivate ? 'flex-end' : 'flex-start', transition: 'background .2s' }}>
                      <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }} />
                    </button>
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--text-3)', margin: '10px 4px 0', lineHeight: 1.55 }}>
                    Quand ton compte est <strong>public</strong>, ton profil et ton activité (tournois, équipes) sont visibles par tout le monde, et on peut te suivre immédiatement.
                    <br /><br />
                    Quand ton compte est <strong>privé</strong>, seuls tes abonnés approuvés voient ton activité — tu valides chaque demande d'abonnement.
                  </p>
                </div>

                {followRequests.length > 0 && (
                  <>
                    <SectionTitle>Demandes d'abonnement</SectionTitle>
                    <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {followRequests.map(rq => (
                        <div key={rq.id} className="card row" style={{ padding: 12, alignItems: 'center', gap: 10 }}>
                          <span style={{ flex: 1, fontSize: 14, fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rq.firstName} {rq.lastName}</span>
                          <button type="button" onClick={() => respondRequest(rq.id, true)} className="btn btn-primary btn-sm">Accepter</button>
                          <button type="button" onClick={() => respondRequest(rq.id, false)} style={{ padding: '8px 12px', borderRadius: 10, background: 'var(--ink-3)', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer', color: 'var(--text-2)' }}>Refuser</button>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Messagerie */}
                <SectionTitle>Messagerie privée</SectionTitle>
                <div style={{ padding: '0 20px' }}>
                  <div className="card col gap-1" style={{ padding: 14 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>Qui peut m'envoyer un message ?</span>
                    {[['all','Tout le monde'],['group_only',"Membres d'un groupe commun"]].map(([val, lbl], i, arr) => (
                      <button key={val} type="button" onClick={() => setChatPreference(val)} style={{
                        display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 12,
                        padding: '11px 0', fontSize: 13, fontWeight: 500,
                        borderBottom: i < arr.length - 1 ? '1px solid var(--line)' : 'none',
                        color: chatPreference === val ? 'var(--sun-2)' : 'var(--text)',
                        background: 'none', border: 'none',
                        borderBottomWidth: i < arr.length - 1 ? 1 : 0,
                        borderBottomStyle: 'solid', borderBottomColor: 'var(--line)',
                        width: '100%', textAlign: 'left', cursor: 'pointer',
                      }}>
                        <span style={{
                          width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                          border: '2px solid ' + (chatPreference === val ? 'var(--sun)' : 'var(--line-2)'),
                          background: chatPreference === val ? 'var(--sun)' : 'transparent',
                        }} />
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Connexions */}
                <SectionTitle>Connexions</SectionTitle>
                <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {googleError && <span style={{ fontSize: 12, color: '#E5503E', fontWeight: 600 }}>{googleError}</span>}
                  {user.hasGoogle ? (
                    <div className="card row" style={{ padding: 14, alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Google · connecté</span>
                      <button type="button" onClick={handleGoogleUnlink} disabled={googleLoading}
                        style={{ padding: '8px 12px', borderRadius: 10, background: 'var(--ink-3)', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer', color: 'var(--text-2)' }}>
                        Dissocier
                      </button>
                    </div>
                  ) : (
                    <GoogleButton onCredential={handleGoogleLink} text="continue_with" />
                  )}
                </div>

                {/* Mot de passe */}
                <SectionTitle>Mot de passe</SectionTitle>
                <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button type="button" onClick={() => setShowPassword(v => !v)} className="card row gap-3"
                    style={{ padding: 14, textAlign: 'left', width: '100%', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Changer le mot de passe</span>
                    <span style={{ color: 'var(--text-3)' }}>{showPassword ? '▲' : '▼'}</span>
                  </button>
                  {showPassword && (
                    <>
                      <SField label="Mot de passe actuel" type="password" placeholder="••••••••" value={currentPwd} onChange={e => setCurrentPwd(e.target.value)} />
                      <SField label="Nouveau mot de passe" type="password" placeholder="8 caractères min." value={newPwd} onChange={e => setNewPwd(e.target.value)} />
                      <SField label="Confirmer le mot de passe" type="password" placeholder="••••••••" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} />
                    </>
                  )}
                </div>

                <div style={{ padding: '20px 20px 24px' }}>
                  <button type="submit" className="btn btn-primary" disabled={saving} style={{ width: '100%', padding: '15px 20px' }}>
                    {saving ? 'Sauvegarde…' : 'Sauvegarder'}
                  </button>
                </div>
              </form>
            </div>
          </>
        ) : (
          /* ──────────────── VIEW MODE ──────────────── */
          <>
            {/* Hero */}
            <div className="tourney-hero sand-grain on-hero" style={{ padding: '0 20px 16px', flexShrink: 0 }}>
              <div className="hero-photo photo-court" />
              <div className="hero-tint" />
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 16px' }}>
                <button aria-label="Modifier le profil" onClick={() => setEditMode(true)} style={{ width: 40, height: 40, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(8px)', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 20h16M14 4l4 4-9 9H5v-4z"/>
                  </svg>
                </button>
                <button aria-label="Se déconnecter" onClick={() => { if (!window.confirm('Se déconnecter ?')) return; onLogout(); }} style={{ width: 40, height: 40, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(8px)', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>
                  </svg>
                </button>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: 60, height: 60, borderRadius: '50%',
                  border: '3px solid rgba(255,255,255,0.4)', overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: avatar ? 'transparent' : avatarGrad, margin: '0 auto' }}>
                  {avatar
                    ? <img src={avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <span className="display" style={{ fontSize: 22, color: '#fff' }}>{initials}</span>
                  }
                </div>
                <div className="display" style={{ fontSize: 26, marginTop: 10 }}>{user.firstName}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {level && <span className="chip" style={{ color: 'rgba(255,255,255,0.9)', background: 'rgba(255,255,255,0.18)', borderColor: 'rgba(255,255,255,0.3)' }}>{LEVEL_LABELS[level] || level}</span>}
                  {position && <span className="chip" style={{ color: 'rgba(255,255,255,0.9)', background: 'rgba(255,255,255,0.18)', borderColor: 'rgba(255,255,255,0.3)' }}>{position.charAt(0).toUpperCase() + position.slice(1)}</span>}
                  {user.isOrganizer && (
                    <a href={process.env.REACT_APP_ORGANIZER_SITE_URL || 'http://localhost:3001'} target="_blank" rel="noreferrer"
                      className="chip" style={{ color: 'var(--ink)', background: 'var(--sun)', borderColor: 'var(--sun)', fontWeight: 700, textDecoration: 'none' }}>
                      🏐 Espace organisateur
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="screen-scroll" style={{ flex: 1 }}>
              {/* Stats row */}
              <div style={{ padding: '16px 20px 0' }}>
                <div className="card row" style={{ padding: 4 }}>
                  {[
                    { v: participatedTournaments.length || '—', l: 'Tournois', color: 'var(--text)' },
                    { v: winRate, l: 'Win rate', color: 'var(--text)' },
                    { v: tournamentsWon || '—', l: 'Remportés', color: 'var(--sun-2)', icon: true },
                  ].map((s, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--line)' }} />}
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 0' }}>
                        <span className="display" style={{ fontSize: 22, color: s.color, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {s.icon && <TrophyIcon size={16} />}{s.v}
                        </span>
                        <span className="mono" style={{ color: 'var(--text-3)', marginTop: 2 }}>{s.l}</span>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Tabs */}
              <div style={{ padding: '14px 20px 0', borderBottom: '1px solid var(--line)' }}>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[['stats','Stats'],['groups','Groupes']].map(([id, lbl]) => (
                    <button key={id} onClick={() => setProfileTab(id)} style={{
                      padding: '10px 14px', position: 'relative', fontSize: 14, fontWeight: 600,
                      color: profileTab === id ? 'var(--text)' : 'var(--text-3)',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                    }}>
                      {lbl}
                      {profileTab === id && <span style={{ position: 'absolute', bottom: -1, left: 14, right: 14, height: 2, background: 'var(--sun)', borderRadius: 2 }} />}
                    </button>
                  ))}
                </div>
              </div>

              {profileTab === 'stats' && (
                <>
                  {/* Performance par format + surface */}
                  {byFormatSurface.length > 0 && (
                    <>
                      <div className="section-title" style={{ marginTop: 24 }}>
                        <h3>Performance par format</h3>
                        <span className="mono" style={{ color: 'var(--text-3)' }}>{byFormatSurface.length}</span>
                      </div>
                      <div className="section col gap-2">
                        {byFormatSurface.map((d, i) => <PerformanceRow key={i} d={d} />)}
                      </div>
                    </>
                  )}

                  {/* Tournois à venir */}
                  {upcomingTournaments.length > 0 && (
                    <>
                      <div className="section-title" style={{ marginTop: 24 }}>
                        <h3>Tournois à venir</h3>
                        <span className="mono" style={{ color: 'var(--text-3)' }}>{upcomingTournaments.length}</span>
                      </div>
                      <div className="section col gap-2">
                        {upcomingTournaments.map((entry, i) => {
                          const t = entry.tournament || entry;
                          const dateStr = t.date ? new Date(t.date + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '';
                          const loc = t.location ? t.location.split(', ').slice(-1)[0] : '';
                          return (
                            <div key={i} className="card row gap-3" style={{ padding: 12, cursor: 'pointer' }}
                              onClick={() => t.id && navigate(`/tournaments/${t.id}`)}>
                              <div style={{
                                width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                                background: 'rgba(10,143,168,0.14)',
                                color: 'var(--cyan)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                                  <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                                </svg>
                              </div>
                              <div className="col" style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                                <span className="mono" style={{ color: 'var(--text-3)', marginTop: 2 }}>
                                  {dateStr}{loc ? ` · ${loc}` : ''}{t.playerFormat ? ` · ${t.playerFormat}` : ''}
                                </span>
                              </div>
                              <span className="chip chip-cyan" style={{ flexShrink: 0 }}>À venir</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {/* Partenaires fréquents */}
                  {profileStats?.partners?.length > 0 && (
                    <>
                      <div className="section-title" style={{ marginTop: 24 }}>
                        <h3>Partenaires fréquents</h3>
                      </div>
                      <div className="section col gap-2">
                        {profileStats.partners.map((p, i) => (
                          <div key={p.id || i} className="card row gap-3" style={{ padding: 12, cursor: 'pointer' }}
                            onClick={() => p.id && navigate(`/profil/${p.id}`)}>
                            <div className="av-circle av-sm" style={{ flexShrink: 0 }}>
                              {p.avatarUrl
                                ? <img src={p.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                : (p.firstName?.[0] || '?') + (p.lastName?.[0] || '')}
                            </div>
                            <div className="col" style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ fontSize: 14, fontWeight: 600 }}>{p.firstName} {p.lastName}</span>
                              <span className="mono" style={{ color: 'var(--text-3)', marginTop: 2 }}>
                                {p.tournaments} tournoi{p.tournaments !== 1 ? 's' : ''} ensemble
                              </span>
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--lime, #6BAA1E)' }}>{p.wins}V</div>
                              <div style={{ fontSize: 11, color: '#E5503E' }}>{p.losses}D</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Historique des tournois — passés seulement */}
                  {pastTournaments.length > 0 ? (
                    <>
                      <div className="section-title" style={{ marginTop: 24 }}>
                        <h3>Historique</h3>
                        <span className="mono" style={{ color: 'var(--text-3)' }}>{pastTournaments.length}</span>
                      </div>
                      <div className="section col gap-2">
                        {pastTournaments.map((entry, i) => {
                          const t = entry.tournament || entry;
                          const podium = entry.myTeam?.results?.placement || entry.placement;
                          const wins   = entry.myTeam?.results?.wins   || 0;
                          const losses = entry.myTeam?.results?.losses || 0;
                          const dateStr = t.date ? new Date(t.date + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
                          const fmt = entry.myTeam?.playerFormat || t.playerFormat || t.playerFormats?.[0];
                          const gen = entry.myTeam?.gender || t.gender;
                          const meta = [dateStr, fmt, gen && (GENDER_LBL[gen] || gen), t.surface && (SURF_LBL[t.surface] || t.surface)].filter(Boolean).join(' · ');
                          return (
                            <div key={i} className="card row gap-3" style={{ padding: 12, cursor: 'pointer' }}
                              onClick={() => t.id && navigate(`/tournaments/${t.id}`)}>
                              <div style={{
                                width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                                background: podium === '1' ? 'rgba(245,166,35,0.18)' : 'var(--ink-3)',
                                color: podium === '1' ? 'var(--sun-2)' : 'var(--text-3)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                {podium ? <TrophyIcon size={18} /> : (
                                  <span className="mono" style={{ fontSize: 9 }}>
                                    {t.date ? new Date(t.date + 'T12:00:00').toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase() : '—'}
                                  </span>
                                )}
                              </div>
                              <div className="col" style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                                <span className="mono" style={{ color: 'var(--text-3)', marginTop: 2 }}>
                                  {meta}
                                </span>
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                {podium && (
                                  <div style={{ fontSize: 13, fontWeight: 700, color: podium === '1' ? 'var(--sun-2)' : 'var(--text-2)', marginBottom: 2 }}>
                                    {podium === '1' ? '🏆 1er' : podium === '2' ? '🥈 2e' : podium === '3' ? '🥉 3e' : `#${podium}`}
                                  </div>
                                )}
                                {(wins > 0 || losses > 0) && (
                                  <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)' }}>{wins}V {losses}D</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : byFormatSurface.length === 0 && upcomingTournaments.length === 0 && (
                    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                      <div style={{ fontSize: 40, marginBottom: 12 }}>🏐</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>Aucun tournoi</div>
                      <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Rejoins ton premier tournoi depuis la page Trouver.</div>
                    </div>
                  )}
                </>
              )}

              {profileTab === 'groups' && (
                <>
                  <div className="section-title" style={{ marginTop: 20 }}>
                    <h3>Mes groupes</h3>
                    <span className="mono" style={{ color: 'var(--text-3)' }}>{userGroups.length}</span>
                  </div>
                  {userGroups.length > 0 ? (
                    <div className="section col gap-3">
                      {userGroups.map((g, i) => (
                        <GroupCard key={g.id || i} g={g} currentUserId={user?.id} manageable onChanged={loadUserGroups} />
                      ))}
                    </div>
                  ) : (
                    <div style={{ padding: '32px 20px 16px', textAlign: 'center' }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>Aucun groupe</div>
                      <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Crée ou rejoins un groupe pour jouer avec tes amis.</div>
                    </div>
                  )}

                  <div className="section-title" style={{ marginTop: 24 }}>
                    <h3>Ajouter un groupe</h3>
                  </div>
                  <div className="section row gap-2">
                    <button className="btn btn-ghost btn-sm" style={{ flex: 1 }}
                      onClick={() => setGroupModal('join')}>+ Rejoindre</button>
                    <button className="btn btn-primary btn-sm" style={{ flex: 1 }}
                      onClick={() => setGroupModal('create')}>+ Créer un groupe</button>
                  </div>
                </>
              )}
              <div style={{ height: 28 }} />
            </div>
          </>
        )}
      </div>

      {groupModal && (
        <GroupFormModal
          mode={groupModal}
          onClose={() => setGroupModal(null)}
          onDone={loadUserGroups}
        />
      )}
    </>
  );
}

export default Profile;
